import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { e4Format, e4Formats } from 'helpers/dates';

import { load, add, update, remove } from 'actions/admin';
import { sendInvite } from 'actions/profile';
import { userUpdated } from 'actions/user';
import { setShowSuccess, setSaving } from 'actions/ui';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import BlobImage from 'components/blobImage';
import { ArrayHelpers, DeleteWarning, Forms, Help, Picker, StringHelpers, SystemText, DebugInfo } from 'e4shared';
import { apiGet } from 'services';
import TableListHover from 'components/admin/tableListHover';
import Groups from './components/groups';

export const AdminUsers = props => {
    const dispatch = useDispatch();

    const text = {
        warning: {
            default: SystemText('Admin/User/Warning/Default'),
            update: SystemText('Admin/User/Warnings/Update'),
            user: x => SystemText('Admin/User/Warning/User', x),
            lastInvite: (d, t) => SystemText('Admin/User/Warning/LastInvite', d, t),
        },
        syncWarning: {
            pre: SystemText('Admin/User/SyncWarning/Pre'),
            blue: SystemText('Admin/User/SyncWarning/Blue'),
            post: SystemText('Admin/User/SyncWarning/Post'),
        },
        licences: {
            full: SystemText('General/Licences/Full'),
            zero: SystemText('General/Licences/Zero'),
        },
        table: {
            options: {
                user: {
                    label: SystemText('Admin/User/Table/Options/User/Label'),
                    delete: SystemText('Admin/User/Table/Options/User/Delete'),
                },
                send: {
                    label: SystemText('Admin/User/Table/Options/Send/Label'),
                },
                group: {
                    label: SystemText('Admin/User/Table/Options/Group/Label'),
                },
                activate: x => SystemText('Admin/User/Table/Options/Activate', x),
                deactivate: x => SystemText('Admin/User/Table/Options/Deactivate', x),
            },
            columns: {
                user: SystemText('Admin/User/Table/Columns/User'),
                email: SystemText('Admin/User/Table/Columns/Email'),
                departments: SystemText('Admin/User/Table/Columns/Departments'),
                locations: SystemText('Admin/User/Table/Columns/Locations'),
                units: SystemText('Admin/User/Table/Columns/BusinessUnits'),
                manager: SystemText('Admin/User/Table/Columns/Manager'),
                collection: SystemText('Admin/User/Table/Columns/Collection'),
                profileGroup: SystemText('Admin/User/Table/Columns/ProfileGroup'),
                coaching: SystemText('Admin/User/Table/Columns/Coaching'),
                permission: SystemText('Admin/User/Table/Columns/Permission'),
                levels: {
                    admin: SystemText('Admin/User/Table/Columns/Permission/Admin'),
                    config: SystemText('Admin/User/Table/Columns/Permission/Config'),
                },
                staff: SystemText('Admin/User/Table/Columns/Staff'),
                roles: SystemText('Admin/User/Table/Columns/Roles'),
                licence: SystemText('Admin/User/Table/Columns/Licence'),
                created: SystemText('Admin/User/Table/Columns/Created'),
                invited: SystemText('Admin/User/Table/Columns/Invited'),
            },
        },
        department: {
            label: SystemText('Admin/User/Department/Label'),
            button: SystemText('Admin/User/Department/Button'),
        },
        fields: {
            firstName: {
                label: SystemText('Admin/User/Fields/FirstName'),
            },
            lastName: {
                label: SystemText('Admin/User/Fields/LastName'),
            },
            licence: {
                label: SystemText('Admin/User/Fields/Licence/Label'),
                help: SystemText('Admin/User/Fields/Licence/Help'),
            },
            email: {
                label: SystemText('Admin/User/Fields/Email/Label'),
                help: SystemText('Admin/User/Fields/Email/Help'),
                unique: SystemText('Admin/User/Fields/Email/Unique'),
            },
            department: {
                label: SystemText('Admin/User/Fields/Department/Label'),
                placeholder: SystemText('Admin/User/Fields/Department/Placeholder'),
            },
            profileGroups: {
                label: SystemText('Admin/User/Fields/ProfileGroups/Label'),
                placeholder: SystemText('Admin/User/Fields/ProfileGroups/Placeholder'),
            },
            subDepartment: {
                label: SystemText('Admin/User/Fields/SubDepartment/Label'),
                help: SystemText('Admin/User/Fields/SubDepartment/Help'),
                placeholder: SystemText('Admin/User/Fields/SubDepartment/Placeholder'),
            },
            job: {
                label: SystemText('Admin/User/Fields/Job/Label'),
            },
            altEmail: {
                label: SystemText('Admin/User/Fields/AltEmail/Label'),
                placeholder: SystemText('Admin/User/Fields/AltEmail/Placeholder'),
                help: SystemText('Admin/User/Fields/AltEmail/Help'),
                unique: SystemText('Admin/User/Fields/AltEmail/Unique'),
            },
            manager: {
                label: SystemText('Admin/User/Fields/Manager/Label'),
                placeholder: SystemText('Admin/User/Fields/Manager/Placeholder'),
            },
            location: {
                label: SystemText('Admin/User/Fields/Location/Label'),
                placeholder: SystemText('Admin/User/Fields/Location/Placeholder'),
            },
            unit: {
                label: SystemText('Admin/User/Fields/Unit/Label'),
                placeholder: SystemText('Admin/User/Fields/Unit/Placeholder'),
            },
            language: {
                label: SystemText('Admin/User/Fields/Language/Label'),
                placeholder: SystemText('Admin/User/Fields/Language/Placeholder'),
            },
            coach: {
                label: SystemText('Admin/User/Fields/Coach/Label'),
                help: SystemText('Admin/User/Fields/Coach/Help'),
                placeholder: SystemText('Admin/User/Fields/Coach/Placeholder'),
            },
            roles: {
                label: SystemText('Admin/User/Fields/Roles/Label'),
                placeholder: SystemText('Admin/User/Fields/Roles/Placeholder'),
            },
            avatar: {
                label: SystemText('Admin/User/Fields/Avatar/Label'),
                placeholder: SystemText('Admin/User/Fields/Avatar/Placeholder'),
            },
            admin: {
                label: SystemText('Admin/User/Fields/Admin/Label'),
                help: SystemText('Admin/User/Fields/Admin/Help'),
            },
            sysconfig: {
                label: SystemText('Admin/User/Fields/SysConfig/Label'),
                help: SystemText('Admin/User/Fields/SysConfig/Help'),
            },
            start: {
                label: SystemText('Admin/User/Fields/Start/Label'),
                help: SystemText('Admin/User/Fields/Start/Help'),
            },
            notes: {
                label: SystemText('Admin/User/Fields/Send/Label'),
                help: SystemText('Admin/User/Fields/Send/Help'),
            },
            active: {
                label: SystemText('Admin/User/Fields/Active/Label'),
                help: SystemText('Admin/User/Fields/Active/Help'),
            },
        },
        buttons: {
            bulk: SystemText('Admin/User/Buttons/Bulk'),
            invite: SystemText('Admin/User/Buttons/Invite'),
        },
        filter: {
            help: SystemText('Admin/User/Filter/Help'),
            all: SystemText('Admin/User/Filter/All'),
            active: SystemText('Admin/User/Filter/Active'),
        },
        warnings: {
            email: {
                option: SystemText('Admin/User/Warnings/Email'),
            },
            add: SystemText('Admin/User/Warnings/Add'),
            update: SystemText('Admin/User/Warnings/Update'),
        },
        pickers: {
            coach: {
                entity: SystemText('Admin/User/Pickers/Coaching/Entity'),
                title: x => SystemText('Admin/User/Pickers/Coaching/Title', x),
                success: SystemText('Admin/User/Pickers/Coaching/Success'),
            },
            roles: {
                entity: SystemText('Admin/User/Pickers/Roles/Entity'),
                title: x => SystemText('Admin/User/Pickers/Roles/Title', x),
                success: SystemText('Admin/User/Pickers/Roles/Success'),
            },
            departments: {
                entity: SystemText('Admin/User/Pickers/Departments/Entity'),
                title: x => SystemText('Admin/User/Pickers/Departments/Title', x),
                success: SystemText('Admin/User/Pickers/Departments/Success'),
            },
            locations: {
                entity: SystemText('Admin/User/Pickers/Locations/Entity'),
                title: x => SystemText('Admin/User/Pickers/Locations/Title', x),
                success: SystemText('Admin/User/Pickers/Locations/Success'),
            },
            businessUnits: {
                entity: SystemText('Admin/User/Pickers/BusinessUnits/Entity'),
                title: x => SystemText('Admin/User/Pickers/BusinessUnits/Title', x),
                success: SystemText('Admin/User/Pickers/BusinessUnits/Success'),
            },
            staff: {
                entity: SystemText('Admin/User/Pickers/Staff/Entity'),
                title: x => SystemText('Admin/User/Pickers/Staff/Title', x),
                success: SystemText('Admin/User/Pickers/Staff/Success'),
            },
            invite: {
                entity: SystemText('Admin/User/Pickers/Invite/Entity'),
                signup: x => SystemText('Admin/User/Pickers/Invite/SignUp', x),
                turnOn: SystemText('Admin/User/Pickers/Invite/TurnOn'),
                send: SystemText('Admin/User/Pickers/Invite/Send'),
                button: SystemText('Admin/User/Pickers/Invite/Button/Send'),
                last: x => SystemText('Admin/User/Pickers/Invite/Last', x),
                never: SystemText('Admin/User/Pickers/Invite/Never'),
            },
        },
    };

    const [currentUser, setCurrentUser] = React.useState(null);
    const [showEmailWarning, setShowEmailWarning] = React.useState(false);
    const [warningMessage, setWarningMessage] = React.useState(text.warning.default);
    const [warningState, setWarningState] = React.useState(null);
    const [pickerProfile, setPickerProfile] = React.useState(null);
    const [showRolePicker, setShowRolePicker] = React.useState(false);
    const [showDepartmentPicker, setShowDepartmentPicker] = React.useState(false);
    const [showLocationPicker, setShowLocationPicker] = React.useState(false);
    const [showBusinessUnitPicker, setShowBusinessUnitPicker] = React.useState(false);
    const [showCoachPicker, setShowCoachPicker] = React.useState(false);
    const [showStaffPicker, setShowStaffPicker] = React.useState(false);
    const [showAddToGroup, setShowAddToGroup] = React.useState(false);
    const [showInvites, setShowInvites] = React.useState(false);
    const [showLicenceWarning, setShowLicenceWarning] = React.useState(false);
    const [showUpdateLicenceWarning, setShowUpdateLicenceWarning] = React.useState(false);
    const [showDepartment, setShowDepartment] = React.useState(false);
    const [inviteAll, setInviteAll] = React.useState(true);
    const [notifications, setNotifications] = React.useState(true);
    const [languages, setLanguages] = React.useState([]);
    const [groups, setGroups] = React.useState([]);

    const user = useSelector(state => state.user.user);
    const isLearn2 = user?.companySettings?.isLearn2;

    const departments = useSelector(state => state.admin.departments) || [];
    const roles = useSelector(state => state.admin.roles) || [];
    const locations = useSelector(state => state.admin.locations) || [];
    const businessUnits = useSelector(state => state.admin.businessUnits) || [];
    const allProfiles = useSelector(state => state.admin.profiles) || [];

    const data = allProfiles?.map(p => ({
        ...p,
        zero: p.licence === 'ZERO',
    }));

    const activeUsers = allProfiles.filter(p => p.active);

    let sync = user.companySettings.scimSync || [];
    if (user.integrations.some(i => i.ref === 'PEOPLEHR')) {
        sync = [
            'Email',
            'Firstname',
            'Lastname',
            'Manager',
            'Department',
            'Location',
            'Roles',
            'Start',
            'Job',
            'Licence',
            'Active',
            'StartDate',
        ];
    }

    const coach = user.companySettings.development.allowCoaches;

    React.useEffect(() => {
        api.load();
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'AdminUsers');
    }, []);

    const api = {
        load: () => {
            dispatch(load('profile'));
            dispatch(load('department'));
            dispatch(load('business-unit'));
            dispatch(load('location'));
            apiGet('language', user.guid, response => setLanguages(response.filter(l => l.active)));
            apiGet('profile-groups', user.guid, response => setGroups(response));
            dispatch(load('role'));
        },
        loadDepartments: () => dispatch(load('department')),
        loadProfile: () => dispatch(load('profile')),
        add: (entity, callback) => {
            dispatch(
                add('profile', entity, ['avatar'], () => {
                    callback?.();
                })
            );
        },
        addDepartment: (entity, callback) =>
            dispatch(
                add('department', entity, null, () => {
                    callback?.();
                    api.loadDepartments();
                })
            ),
        update: (entity, callback, bulk) => {
            dispatch(
                update(
                    'profile',
                    entity,
                    ['avatar'],
                    () => {
                        user.id === entity.id && dispatch(userUpdated(entity));
                        callback?.();
                    },
                    !bulk
                )
            );
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                dispatch(remove('profile', id, () => idx + 1 === ids.length && callback()));
            });
        },
        sendInvite: (id, notifications) => {
            dispatch(sendInvite(id, notifications));
        },
    };

    const licenseOveruse = entity => {
        const used = Array.isArray(data) ? data.filter(d => d.active && !d.zero).length : 0;
        const zeroUsed = Array.isArray(data) ? data.filter(d => d.active && d.zero).length : 0;

        return (
            user &&
            entity.active &&
            ((entity.licence === 'FULL' && user.licences <= used) ||
                (entity.licence === 'ZERO' && user.zero_Licences <= zeroUsed))
        );
    };

    const addUser = (entity, callback) => {
        if (licenseOveruse(entity)) {
            setShowLicenceWarning(true);
            setWarningState({ entity, callback });
        } else {
            api.add(entity, callback);
        }
    };

    const addConfirm = confirm => {
        setShowLicenceWarning(false);
        if (confirm) {
            api.add(warningState.entity, warningState.callback);
        }
    };

    const updateUser = (entity, callback, bulk) => {
        const thisProfile = data.filter(d => d.id === entity.id)[0];
        if (licenseOveruse(entity) && !thisProfile.active) {
            setShowUpdateLicenceWarning(true);
            setWarningState({ entity, callback, bulk });
        } else {
            api.update(entity, callback, bulk);
        }
    };

    const updateConfirm = confirm => {
        setShowUpdateLicenceWarning(false);
        if (confirm) {
            api.update(warningState.entity, warningState.callback, warningState.bulk);
        } else {
            dispatch(setSaving(false));
        }
    };

    const showEmailWarningMessage = user => {
        const warningMessage = `${text.warning.user(user.full_Name)} ${
            user.lastInvite
                ? `\n- ${text.warning.lastInvite(e4Format(user.lastInvite), e4Format(user.lastInvite, e4Formats.time))}`
                : ''
        }`;

        setCurrentUser(user);
        setWarningMessage(warningMessage);
        setShowEmailWarning(true);
    };

    const sendEmail = (confirm, selected, notifications) => {
        if (confirm) {
            api.sendInvite(currentUser.id, selected, notifications);
        }

        setCurrentUser(null);
        setShowEmailWarning(false);
    };

    if (!user.sys_Config) props.history.replace('/');

    const options = {
        ...DefaultOptions(),
        sortBy: 'list_Name',
        showActive: true,
        label: text.table.options.user.label,
        labelOverride: 'full_Name',
        mainColumnIndex: 1,
        cannotDeleteMessage: text.table.options.user.delete,
        showBulk: true,
        extraOptions: [
            {
                btnClass: 'btn-primary',
                label: text.table.options.send.label,
                icon: 'fa-envelope',
                disabled: d => !d.active || d.zero,
                onClick: d => showEmailWarningMessage(d),
            },
        ],
    };

    if (isLearn2) {
        options.extraOptions.push({
            btnClass: 'btn-primary',
            bulk: text.table.options.group.label,
            hideStandard: true,
            icon: 'fa-plus',
            onClick: d => setShowAddToGroup(d),
        });
    }

    const departmentOptions = {
        ...DefaultOptions(),
        label: text.department.label,
    };

    const addDepartment = () => {
        return (
            <div className="mtm10 mb10">
                <button className="btn btn-link text-sm" onClick={() => setShowDepartment(true)}>
                    {text.department.button}
                </button>
            </div>
        );
    };

    let fields = [
        {
            label: text.fields.firstName.label,
            type: 'text',
            mandatory: true,
            dataProperty: 'first_Name',
            maxLength: 50,
            generalErrors: true,
            className: sync.some(s => s === 'Firstname') ? 'synced' : '',
        },
        {
            label: text.fields.lastName.label,
            type: 'text',
            mandatory: true,
            dataProperty: 'last_Name',
            maxLength: 50,
            generalErrors: true,
            className: sync.some(s => s === 'Lastname') ? 'synced' : '',
        },
        {
            label: text.fields.email.label,
            type: 'email',
            mandatory: true,
            dataProperty: 'email',
            maxLength: 150,
            uniqueErrors: true,
            uniqueErrors_Text: text.fields.email.unique,
            help: text.fields.email.help,
            regEx: StringHelpers.regExs.EMAIL,
            className: sync.some(s => s === 'Email') ? 'synced' : '',
        },
        {
            label: text.fields.department.label,
            type: 'select',
            dataProperty: 'department_Ids',
            data: departments,
            allowMultiple: true,
            isTree: true,
            placeholder: text.fields.department.placeholder,
            className: sync.some(s => s === 'Department') ? 'synced' : '',
        },
        {
            type: 'special',
            renderer: () => addDepartment(),
        },
        {
            label: text.fields.job.label,
            type: 'text',
            dataProperty: 'job_Title',
            maxLength: 50,
            className: sync.some(s => s === 'Job') ? 'synced' : '',
        },
        {
            label: text.fields.profileGroups.label,
            type: 'select',
            dataProperty: 'profile_Group_Ids',
            data: groups.filter(g => g.system_Group_Name !== 'all_users'),
            addOnly: false,
            allowMultiple: true,
            requiresIsLearn2: true,
            placeholder: text.fields.profileGroups.placeholder,
        },
        {
            label: text.fields.altEmail.label,
            type: 'textarea',
            dataProperty: 'alt_Emails',
            rows: 3,
            placeholder: text.fields.altEmail.placeholder,
            help: text.fields.altEmail.help,
            regEx: StringHelpers.regExs.EMAIL,
            regExPerLine: true,
            uniqueErrors: true,
            uniqueErrors_Text: text.fields.altEmail.unique,
            inTitleBar: true,
        },
        {
            label: text.fields.manager.label,
            type: 'select',
            dataProperty: 'manager_Id',
            dataBlock: 'id',
            data: data.filter(u => u.active).map(u => ({ ...u, id: u.id, name: u.list_Name })),
            placeholder: text.fields.manager.placeholder,
            className: sync.some(s => s === 'Manager') ? 'synced' : '',
        },
        {
            label: text.fields.coach.label,
            type: 'select',
            dataProperty: 'coach_Id',
            dataBlock: 'id',
            data: data.filter(u => u.active).map(u => ({ ...u, id: u.id, name: u.list_Name })),
            placeholder: text.fields.coach.placeholder,
        },
        {
            label: text.fields.location.label,
            type: 'select',
            dataProperty: 'location_Ids',
            data: locations,
            isTree: locations.some(x => x.parent_Id),
            allowMultiple: true,
            placeholder: text.fields.location.placeholder,
            inTitleBar: true,
            className: sync.some(s => s === 'Location') ? 'synced' : '',
        },
        {
            label: text.fields.unit.label,
            type: 'select',
            dataProperty: 'business_Unit_Ids',
            data: businessUnits,
            isTree: businessUnits.some(x => x.parent_Id),
            allowMultiple: true,
            placeholder: text.fields.unit.placeholder,
            inTitleBar: true,
            className: sync.some(s => s === 'BusinessUnit') ? 'synced' : '',
        },
        {
            label: text.fields.language.label,
            type: 'select',
            dataProperty: 'language_Id',
            data: languages,
            placeholder: text.fields.language.placeholder,
            inTitleBar: true,
        },
        {
            label: text.fields.roles.label,
            type: 'select',
            dataProperty: 'role_Ids',
            data: roles,
            allowMultiple: true,
            placeholder: text.fields.roles.placeholder,
            className: sync.some(s => s === 'Roles') ? 'synced' : '',
        },
        {
            label: text.fields.avatar.label,
            type: 'image',
            folder: 'avatar',
            ratio: 1,
            image: true,
            dataProperty: 'avatar',
            placeholder: text.fields.avatar.placeholder,
            inTitleBar: true,
        },
        {
            label: text.fields.admin.label,
            help: text.fields.admin.help,
            type: 'toggle',
            dataProperty: 'administrator',
            hideField: (values, entity) => values.zero,
            inTitleBar: true,
        },
        {
            label: text.fields.sysconfig.label,
            help: text.fields.sysconfig.help,
            type: 'toggle',
            dataProperty: 'sys_Config',
            hideField: (values, entity) => values.zero,
            inTitleBar: true,
        },
        {
            label: text.fields.start.label,
            help: text.fields.start.help,
            type: 'date',
            dataProperty: 'start_Date',
            mandatory: true,
            allowNull: false,
            className: sync.some(s => s === 'StartDate') ? 'synced' : '',
        },
        {
            label: text.fields.notes.label,
            help: text.fields.notes.help,
            type: 'toggle',
            dataProperty: 'notifications',
            hideField: (values, entity) => values.zero,
            inTitleBar: true,
        },
        {
            label: text.fields.active.label,
            help: text.fields.active.help,
            type: 'toggle',
            dataProperty: 'active',
            inTitleBar: true,
            className: sync.some(s => s === 'Active') ? 'synced' : '',
        },
    ];

    if ((languages || []).length <= 1) {
        fields = fields.filter(f => f.dataProperty !== 'language_Id');
    }

    const departmentFields = [
        {
            label: text.department.label,
            type: 'text',
            dataProperty: 'name',
            mandatory: true,
            maxLength: 50,
            generalErrors: true,
            uniqueErrors: true,
        },
        {
            label: text.fields.subDepartment.label,
            help: text.fields.subDepartment.help,
            type: 'select',
            dataProperty: 'parent_Id',
            isTree: true,
            data: departments.filter(d => !d.partent_Id),
            dataBlock: 'id',
            allowMultiple: false,
            placeholder: text.fields.subDepartment.placeholder,
        },
    ];

    let tableColumns = [
        {
            dataProperty: 'administrator',
            format: (v, d) => {
                const hover =
                    d.licence === 'ZERO'
                        ? ''
                        : d.administrator
                          ? text.table.columns.levels.admin
                          : d.sys_Config
                            ? text.table.columns.levels.config
                            : '';

                return hover ? (
                    <Help id={`help-admin-${d.id}`} target={<i className="fa fa-user-shield" />}>
                        {hover}
                    </Help>
                ) : (
                    <></>
                );
            },
        },
        {
            label: text.table.columns.user,
            dataProperty: 'list_Name',
            searchProperty: 'full_Name',
            sortByFunction: (data, asc) => {
                const adminPrefix = asc ? '0' : '1'; // Always have admins at top, regardless of direction of sort
                const regPrefix = asc ? '1' : '0';
                const dir = asc ? 1 : -1;

                return data
                    .map(d => {
                        const sortPrefix =
                            d.licence === 'ZERO' ? regPrefix : d.administrator || d.sys_Config ? adminPrefix : regPrefix;

                        return { ...d, sortProperty: `${sortPrefix}-${d.list_Name}` };
                    })
                    .sort((a, b) => (a.sortProperty > b.sortProperty ? 1 * dir : -1 * dir));
            },
            className: 'no-wrap',
        },
        { label: text.table.columns.email, dataProperty: 'email', searchProperty: 'email' },
        {
            label: text.table.columns.manager,
            dataProperty: 'manager_Full_Name',
            className: 'hidden-xs no-wrap',
        },
        {
            label: text.table.columns.collection,
            dataProperty: 'collection_Names',
            className: 'hidden-xs',
            requiresIsLearn2: true,
            format: (x, d) => <TableListHover icon="fa-cube" data={d.collection_Names} uniqueKey={`collection-${d.id}`} />,
        },
        {
            label: text.table.columns.profileGroup,
            dataProperty: 'profile_Group_Names',
            className: 'hidden-xs no-wrap',
            requiresIsLearn2: true,
            format: (x, d) => <TableListHover data={d.profile_Group_Names} uniqueKey={`group-${d.id}`} />,
        },
        {
            label: text.table.columns.invited,
            dataProperty: 'lastInvite',
            className: 'visible-lg no-wrap',
            format: x => (x ? e4Format(x, e4Formats.default) : '-'),
        },
        {
            label: text.table.columns.staff,
            dataProperty: 'staff_Ids',
            showCount: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowStaffPicker(true);
            },
        },
        {
            label: text.table.columns.roles,
            dataProperty: 'role_Ids',
            showCount: true,
            searchProperty: 'role_Names',
            searchPropertyIsArray: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowRolePicker(true);
            },
        },
        {
            label: text.table.columns.departments,
            dataProperty: 'department_Ids',
            showCount: true,
            searchProperty: 'department_Names',
            searchPropertyIsArray: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowDepartmentPicker(true);
            },
        },
        {
            label: text.table.columns.locations,
            dataProperty: 'location_Ids',
            showCount: true,
            searchProperty: 'location_Names',
            searchPropertyIsArray: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowLocationPicker(true);
            },
        },
        {
            label: text.table.columns.units,
            dataProperty: 'business_Unit_Ids',
            showCount: true,
            searchProperty: 'business_Unit_Names',
            searchPropertyIsArray: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowBusinessUnitPicker(true);
            },
        },
        {
            label: text.table.columns.coaching,
            dataProperty: 'coaching_Ids',
            showCount: true,
            onClick: profile => {
                setPickerProfile(profile);
                setShowCoachPicker(true);
            },
        },
    ];

    if (!coach) {
        fields = fields.filter(c => c.dataProperty !== 'coach_Id');
        tableColumns = tableColumns.filter(c => c.dataProperty !== 'coaching_Ids');
    }

    if (!isLearn2) {
        fields = fields.filter(c => c.requiresIsLearn2 !== true);
        tableColumns = tableColumns.filter(c => c.requiresIsLearn2 !== true);
    }

    if (user.zero_Licences > 0) {
        tableColumns.push({
            label: text.table.columns.licence,
            format: x => (x ? text.licences.zero : text.licences.full),
            dataProperty: 'zero',
        });
        fields.push({
            label: text.fields.licence.label,
            help: text.fields.licence.help,
            type: 'toggle',
            dataProperty: 'zero',
            yesLabel: text.licences.zero,
            noLabel: text.licences.full,
        });
    }

    const defaultValues = { active: true, notifications: true, start_Date: new Date() };

    const extraButtons = (
        <>
            <button className="ml5 btn btn-primary" onClick={() => props.history.push('/admin/users-bulk')}>
                <i className="fa fa-file-excel mr5" />
                {text.buttons.bulk}
            </button>
            <button className="ml5 btn btn-primary" onClick={() => setShowInvites(true)}>
                <i className="fa fa-envelope mr5" />
                {text.buttons.invite}
            </button>
        </>
    );

    return (
        <div>
            <SimpleAdmin
                {...props}
                data={data}
                extraButtons={extraButtons}
                add={(entity, callback) => addUser({ ...entity, licence: entity.zero ? 'ZERO' : 'FULL' }, callback)}
                update={(entity, callback, bulk) =>
                    updateUser({ ...entity, licence: entity.zero ? 'ZERO' : 'FULL' }, callback, bulk)
                }
                remove={(id, callback) => api.remove(id, callback)}
                load={() => api.loadProfile()}
                defaultValues={defaultValues}
                fields={fields}
                options={options}
                columns={tableColumns}
                filterHelp={text.filter.help}
                filterTextOn={text.filter.active}
                filterTextOff={text.filter.all}
                filterColumn="active"
                formPrefix={
                    ArrayHelpers.existsAndHasLength(sync) ? (
                        <div className="mb20">
                            <i className="fa fa-warning text-danger mr10" />
                            {text.syncWarning.pre} <div className="inline synced p5">{text.syncWarning.blue}</div>{' '}
                            {text.syncWarning.post}
                        </div>
                    ) : null
                }
            />
            <div id="warnings">
                <DeleteWarning
                    id="emailWarning"
                    message={warningMessage}
                    show={showEmailWarning}
                    option={text.warnings.email.option}
                    optionSelected
                    close={(confirm, selected, notifications) => sendEmail(confirm, selected, notifications)}
                />
                <DeleteWarning
                    id="addWarning"
                    message={text.warnings.add}
                    show={showLicenceWarning}
                    close={confirm => addConfirm(confirm)}
                />
                <DeleteWarning
                    id="updateWarning"
                    message={text.warning.update}
                    show={showUpdateLicenceWarning}
                    close={confirm => updateConfirm(confirm)}
                />
            </div>
            <Picker
                title={text.pickers.roles.title(pickerProfile?.full_Name)}
                entity={text.pickers.roles.entity}
                data={roles}
                nameProperty="name"
                value={pickerProfile?.role_Ids}
                show={showRolePicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, role_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.roles.success));
                        setShowRolePicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowRolePicker(false);
                }}
                itemPrefix="street-view"
            />
            <Picker
                title={text.pickers.departments.title(pickerProfile?.full_Name)}
                entity={text.pickers.departments.entity}
                data={departments}
                nameProperty="name"
                value={pickerProfile?.department_Ids}
                show={showDepartmentPicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, department_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.departments.success));
                        setShowDepartmentPicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowDepartmentPicker(false);
                }}
                itemPrefix="building"
            />

            <Picker
                title={text.pickers.locations.title(pickerProfile?.full_Name)}
                entity={text.pickers.locations.entity}
                data={locations}
                nameProperty="name"
                value={pickerProfile?.location_Ids}
                show={showLocationPicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, location_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.locations.success));
                        setShowLocationPicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowLocationPicker(false);
                }}
                itemPrefix="map-marker"
            />

            <Picker
                title={text.pickers.businessUnits.title(pickerProfile?.full_Name)}
                entity={text.pickers.businessUnits.entity}
                data={businessUnits}
                nameProperty="name"
                value={pickerProfile?.business_Unit_Ids}
                show={showBusinessUnitPicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, business_Unit_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.businessUnits.success));
                        setShowBusinessUnitPicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowBusinessUnitPicker(false);
                }}
                itemPrefix="cubes"
            />
            <Picker
                title={text.pickers.coach.title(pickerProfile?.full_Name)}
                entity={text.pickers.coach.entity}
                data={activeUsers.filter(u => u.id !== pickerProfile?.id)}
                nameProperty="full_Name"
                value={pickerProfile?.coaching_Ids}
                show={showCoachPicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, update_Coaching_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.coach.success));
                        setShowCoachPicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowCoachPicker(false);
                }}
                itemPrefix="whistle"
            />
            <Picker
                title={text.pickers.staff.title(pickerProfile?.full_Name)}
                entity={text.pickers.staff.entity}
                data={activeUsers.filter(u => u.id !== pickerProfile?.id)}
                nameProperty="full_Name"
                filterOption="staff"
                value={pickerProfile?.staff_Ids}
                show={showStaffPicker}
                onSave={ids => {
                    api.update({ ...pickerProfile, update_Staff_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.staff.success));
                        setShowStaffPicker(false);
                        api.loadProfile();
                    });
                }}
                onClose={() => {
                    setPickerProfile(null);
                    setShowStaffPicker(false);
                }}
                itemPrefix={p => (
                    <div className="image-holder mr10">
                        <BlobImage folder="avatar" src={p.avatar} initials={p.initials} />
                    </div>
                )}
            />
            <Picker
                extra={() => {
                    return (
                        <div className="row">
                            <div className="col-md-12">
                                <Forms.Toggle
                                    label={text.pickers.invite.turnOn}
                                    value={notifications}
                                    onChange={value => setNotifications(value)}
                                />
                            </div>
                        </div>
                    );
                }}
                title={text.pickers.invite.send}
                itemHelp={p => (p.lastInvite ? text.pickers.invite.last(e4Format(p.lastInvite)) : text.pickers.invite.never)}
                entity={text.pickers.invite.entity}
                data={activeUsers.filter(u => u.licence === 'FULL')}
                nameProperty="full_Name"
                filterOption="staff"
                value={pickerProfile?.staff_Ids}
                show={showInvites}
                saveLabel={text.pickers.invite.button.send}
                onSave={ids => {
                    ids.forEach(id => api.sendInvite(id, inviteAll, notifications));
                    setShowInvites(false);
                    api.loadProfile();
                }}
                onClose={() => {
                    setShowInvites(false);
                }}
                itemPrefix={p => (
                    <div className="image-holder mr10">
                        <BlobImage folder="avatar" src={p.avatar} initials={p.initials} />
                    </div>
                )}
            />
            <SimpleAdmin
                add={(entity, callback) =>
                    api.addDepartment(entity, () => {
                        setShowDepartment(false);
                        api.loadDepartments();
                        dispatch(setSaving(false));
                    })
                }
                options={departmentOptions}
                fields={departmentFields}
                subAdd
                showModal={showDepartment}
                setShowModal={x => setShowDepartment(x)}
                closeModal={() => setShowDepartment(false)}
            />
            {showAddToGroup && (
                <Groups show={showAddToGroup} close={() => setShowAddToGroup(false)} groups={groups} users={data} />
            )}
        </div>
    );
};

export default AdminUsers;
